import {
  Action,
  combineReducers,
  configureStore,
  ThunkAction,
} from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import {
  authApi,
  companiesApi,
  gradeApi,
  subAdminApi,
  companySubAdmins,
  sectorsApi,
  gradeSetupApi,
  jobFunctionsApi,
  companyJobFunctionsApi,
  businessUnitApi,
  longTermIPApi,
} from "@services";
import { authReducer } from "./auth";
import { businessUnitReducer } from "./business-unit";
import { selectedAppReducer } from "./selected_app";
import { subAdminReducer } from "./sub-admin";
import { countriesReducer } from "./countries";
import { sectorsReducer } from "./sectors";
import { gradeReducer } from "./grade";

import {
  ISectorsState,
  ISubAdminReducer,
  IAuthState,
  IBusinessUnitState,
} from "@types";

export interface ICombineReducerProps {
  auth: IAuthState;
  businessUnit: IBusinessUnitState;
  subAdmin: ISubAdminReducer;
  sectors: ISectorsState;
}

const appReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [companiesApi.reducerPath]: companiesApi.reducer,
  [businessUnitApi.reducerPath]: businessUnitApi.reducer,
  [subAdminApi.reducerPath]: subAdminApi.reducer,
  [gradeSetupApi.reducerPath]: gradeSetupApi.reducer,
  [longTermIPApi.reducerPath]: longTermIPApi.reducer,
  [companySubAdmins.reducerPath]: companySubAdmins.reducer,
  [sectorsApi.reducerPath]: sectorsApi.reducer,
  [gradeApi.reducerPath]: gradeApi.reducer,
  [jobFunctionsApi.reducerPath]: jobFunctionsApi.reducer,
  // JF work
  [companyJobFunctionsApi.reducerPath]: companyJobFunctionsApi.reducer,
  auth: authReducer,
  businessUnit: businessUnitReducer,
  selected_app: selectedAppReducer,
  subAdmin: subAdminReducer,
  sectors: sectorsReducer,
  countries: countriesReducer,
  grade: gradeReducer,
});

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: ["auth", "selected_app"],
};

const rootReducer = (state: any, action: any) => {
  if (
    action?.meta?.arg?.endpointName === "logout" &&
    action?.type === "authApi /executeMutation/fulfilled"
  ) {
    state = {} as RootState;
    localStorage.clear();
  }

  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools:
    process.env.NODE_ENV !== "production" && typeof window !== "undefined",
  middleware: getDefaultMiddleware => [
    ...getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
    authApi.middleware,
    companiesApi.middleware,
    longTermIPApi.middleware,
    gradeSetupApi.middleware,
    jobFunctionsApi.middleware,
    companyJobFunctionsApi.middleware,
    companySubAdmins.middleware,
    gradeApi.middleware,
  ],
});

window.store = store;
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
